<template>
  <v-card>
    <v-card-title class="font-weight-medium secondary--text" :class="isRefund ? 'primary darken-1' : 'primary'">
      <v-icon small color="secondary" class="mr-2">
        {{ isRefund ? 'mdi-cash-refund' : 'mdi-file-document-edit-outline' }}
      </v-icon>
      {{ isRefund ? 'Refund Excess Amount' : 'Apply to Invoice' }}
      <v-spacer />
      <v-btn small icon @click="$emit('close')">
        <v-icon small color="secondary">mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider />

    <!-- ── REFUND FORM ── -->
    <v-card-text v-if="isRefund" class="pt-4">
      <v-form ref="form" lazy-validation>
        <v-row dense>
          <v-col cols="12">
            <v-text-field
              :value="`₹${remaining}`"
              label="Refund Amount"
              outlined
              dense
              hide-details="auto"
              readonly
              disabled
              class="mb-1"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model.trim="alloc.refund_transaction_id"
              label="Transaction ID *"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>

          <v-col cols="6">
            <v-menu
              v-model="datePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedRefundDate"
                  label="Refund Date *"
                  outlined
                  dense
                  hide-details="auto"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-model="alloc.refunded_date"
                :max="new Date().toISOString().substr(0, 10)"
                @input="datePicker = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model.trim="alloc.bank_name"
              label="Bank Name *"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model.trim="alloc.account_number"
              label="Account Number *"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model.trim="alloc.ifsc_code"
              label="IFSC Code *"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model.trim="alloc.remarks"
              label="Remarks"
              outlined
              dense
              hide-details="auto"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <!-- ── APPLY TO INVOICE FORM (shared by excess_payment and tuition_fee_excess —
         both adjust a pending student_invoice; the admit plan itself is only
         ever created once at admission, real dues afterwards are invoices) ── -->
    <v-card-text v-else class="pt-4">
      <v-form ref="form" lazy-validation>
        <!-- Invoice list -->
        <div v-if="invoices.length" class="caption grey--text mb-1">Select a pending invoice *</div>

        <div v-if="invoiceLoading" class="text-center py-4">
          <v-progress-circular indeterminate color="primary" size="28" />
        </div>

        <v-alert v-else-if="invoices.length === 0" dense text type="info" class="mb-3">
          No pending invoices found for this student
        </v-alert>

        <v-sheet v-else outlined rounded class="mb-3" style="max-height: 220px; overflow-y: auto">
          <v-list dense>
            <v-list-item
              v-for="inv in invoices"
              :key="inv.id"
              :input-value="selectedInvoice && selectedInvoice.id === inv.id"
              active-class="primary lighten-5"
              style="cursor: pointer"
              @click="selectInvoice(inv)"
            >
              <v-list-item-content>
                <v-list-item-title class="body-2">
                  {{ inv.particular || inv.type || 'Invoice' }}
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  Due: ₹{{ inv.amount }}
                  <span v-if="inv.to_date"> &nbsp;·&nbsp; {{ formattedToDate(inv.to_date) }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon v-if="selectedInvoice && selectedInvoice.id === inv.id" color="primary" small>
                  mdi-check-circle
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-sheet>

        <!-- Amount to apply -->
        <v-row v-if="invoices.length" dense>
          <v-col cols="6">
            <v-text-field
              v-model="alloc.amount"
              label="Amount to Subtract *"
              outlined
              dense
              hide-details="auto"
              type="number"
              prefix="₹"
              :rules="[rules.required, rules.nonZero, ruleMaxApply]"
              :hint="selectedInvoice ? `Max: ₹${maxApply}` : ''"
              persistent-hint
              :disabled="!selectedInvoice"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.trim="alloc.remarks"
              label="Remarks"
              outlined
              dense
              hide-details="auto"
              :disabled="!selectedInvoice"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-divider/>

    <v-card-actions class="px-4 pb-4 pt-3">
      <v-spacer />
      <v-btn
        :class="isRefund ? 'primary darken-1 white--text' : 'accent white--text'"
        :loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        <v-icon left small>{{ isRefund ? 'mdi-cash-refund' : 'mdi-check' }}</v-icon>
        {{ isRefund ? 'Confirm Refund' : 'Apply to Invoice' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  props: {
    excessPayment: { type: Object, required: true },
    mode: { type: String, default: "refund" },
  },

  data() {
    return {
      alloc: {
        amount: "",
        remarks: "",
        refund_transaction_id: "",
        refunded_date: null,
        bank_name: "",
        account_number: "",
        ifsc_code: "",
      },
      saving: false,
      datePicker: false,
      invoices: [],
      invoiceLoading: false,
      selectedInvoice: null,
      rules,
    };
  },

  computed: {
    isRefund() {
      return this.mode === "refund";
    },

    isTuitionFeeExcess() {
      return this.excessPayment.source === "tuition_fee_excess";
    },

    remaining() {
      if (this.excessPayment.tally) return parseFloat(this.excessPayment.tally.remaining);
      return parseFloat(this.excessPayment.amount);
    },

    maxApply() {
      if (!this.selectedInvoice) return this.remaining;
      return Math.min(this.remaining, parseFloat(this.selectedInvoice.amount));
    },

    ruleMaxApply() {
      return (v) =>
        !v || parseFloat(v) <= this.maxApply || `Cannot exceed ₹${this.maxApply}`;
    },

    canSave() {
      if (this.isRefund) {
        return !!(
          this.alloc.refund_transaction_id &&
          this.alloc.refunded_date &&
          this.alloc.bank_name &&
          this.alloc.account_number &&
          this.alloc.ifsc_code
        );
      }
      const amt = parseFloat(this.alloc.amount);
      return !!(this.selectedInvoice && amt > 0 && amt <= this.maxApply);
    },
    formattedRefundDate: {
      get() {
        if (!this.alloc.refunded_date) return "";
        const [y, m, d] = this.alloc.refunded_date.split("-");
        return `${d}-${m}-${y}`;
      },
      set(val) {
        this.alloc.refunded_date = val ? val : null;
      },
    },
  },

  mounted() {
    if (!this.isRefund) this.loadPendingInvoices();
  },

  methods: {
    async loadPendingInvoices() {
      this.invoiceLoading = true;
      try {
        const base = this.isTuitionFeeExcess
          ? "/tuition_fee_excess"
          : "/excess_payments";
        const res = await this.$axios.get(`${base}/${this.excessPayment.id}/pending_invoices`);
        this.invoices = res.data.data || [];
      } catch (e) {
        console.log(e);
      } finally {
        this.invoiceLoading = false;
      }
    },

    selectInvoice(inv) {
      this.selectedInvoice = inv;
      this.alloc.amount = Math.min(this.remaining, parseFloat(inv.amount)).toString();
    },

    formattedToDate(dateStr) {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
},

    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const idKey = this.isTuitionFeeExcess
          ? "tuition_fee_excess_id"
          : "excess_payment_id";

        const payload = {
          allocation: {
            [idKey]: this.excessPayment.id,
            usage_type: this.mode,
            amount: this.isRefund ? this.remaining : this.alloc.amount,
            remarks: this.alloc.remarks || null,
            ...(this.isRefund
              ? {
                  refund_transaction_id: this.alloc.refund_transaction_id,
                  refunded_date: this.alloc.refunded_date,
                  bank_name: this.alloc.bank_name,
                  account_number: this.alloc.account_number,
                  ifsc_code: this.alloc.ifsc_code,
                }
              : {
                  invoice_id: this.selectedInvoice.id,
                  invoice_type: "student_invoice",
                }),
          },
        };
        const base = this.isTuitionFeeExcess
          ? "/tuition_fee_excess"
          : "/excess_payments";
        await this.$axios.post(`${base}/${this.excessPayment.id}/allocations`, payload);
        this.$store.commit("showSnackbar", {
          message: this.isRefund ? "Refund recorded" : "Applied to invoice",
          color: "success",
        });
        this.$emit("saved");
      } catch (e) {
        const msg =
          e.response?.data?.errors ||
          e.response?.data?.message ||
          "Failed to save";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
